import { Injectable, signal, inject } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { TokenService } from './token.service';
import { CityService } from './city.service';

export interface LiveBus {
  vehicleId: number;
  plate:     string;
  lineId:    number;
  lineName:  string;
  lineColor: string;
  routeId:   number;
  lat:       number | null;
  lng:       number | null;
  heading?:  number;
  tripId:    number;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly tokenService = inject(TokenService);
  private readonly cityService  = inject(CityService);
  private socket: Socket | null = null;

  /** Reactive map: vehicleId → LiveBus */
  readonly buses = signal<Map<number, LiveBus>>(new Map());

  connect(): void {
    if (this.socket?.connected) return;

    this.socket = io(this.cityService.apiBaseUrl(), {
      transports: ['websocket'],
      auth: { token: this.tokenService.getToken() ?? '' },
    });

    this.socket.on('connect', () => {
      console.log('[RealtimeService] Connected to Socket.IO');
    });

    // Backend emits 'location-updated' to 'line-{lineId}' rooms on every driver GPS tick.
    // Payload: { tripId, lineId, lat, lng, ts }
    this.socket.on('location-updated', (data: any) => {
      this.buses.update(map => {
        const next = new Map(map);
        const tripId = Number(data.tripId);
        const existing = next.get(tripId);
        const bus: LiveBus = {
          vehicleId: tripId,           // use tripId as stable key (no vehicleId in payload)
          plate:     existing?.plate ?? '',
          lineId:    Number(data.lineId),
          lineName:  existing?.lineName  ?? '',
          lineColor: existing?.lineColor ?? '#32737E',
          routeId:   existing?.routeId   ?? 0,
          lat:       data.lat,
          lng:       data.lng,
          heading:   data.heading,
          tripId,
          updatedAt: new Date().toISOString(),
        };
        next.set(tripId, bus);
        return next;
      });
    });

    this.socket.on('disconnect', () => {
      console.log('[RealtimeService] Disconnected');
      this.buses.set(new Map());
    });
  }

  /** Join a line room to receive live GPS updates for buses on that line. */
  subscribeToLine(lineId: number): void {
    if (!this.socket) return;
    if (this.socket.connected) {
      this.socket.emit('join-line', lineId);
    } else {
      this.socket.once('connect', () => this.socket!.emit('join-line', lineId));
    }
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.buses.set(new Map());
  }

  /** Estimate arrival minutes for a bus to reach a stop (rough: distance / speed) */
  estimateArrivalMinutes(busLat: number, busLng: number, stopLat: number, stopLng: number): number {
    const R = 6_371_000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(stopLat - busLat);
    const dLng = toRad(stopLng - busLng);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(busLat)) * Math.cos(toRad(stopLat)) *
              Math.sin(dLng / 2) ** 2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    // Average urban bus speed ~200 m/min
    return Math.max(1, Math.round(dist / 200));
  }
}
